/**
 * Relewise-powered product listing — the search results page AND the Relewise
 * category page share this one engine. Loaded when the "Enable Relewise" theme
 * setting is on (search) or "Relewise category pages" is on too (collection);
 * fills the grid with a ProductSearch result using the same per-handle card
 * rendering as the overlay and the recommendation slots.
 *
 *   [data-relewise-listing="search"]      term from the URL, location "Search Results Page",
 *                                         configuration searchConfigurations.search
 *   [data-relewise-listing="collection"]  ProductCategoryIdFilter on data-collection-id,
 *                                         location "Custom Collection Page",
 *                                         configuration searchConfigurations.collection
 *                                         (the app resolves per-collection overrides)
 *
 * Facets + sorting: the Relewise app's Search Configuration decides which
 * facets and sort options exist; assets/relewise-facets.js renders the
 * designed filter chrome from the response and owns the URL contract:
 *   ?q=<term>&f.<key>=<value>…&price=<min>-<max>&sort_by=<sorting id>&page=<n>
 * Every facet/sort interaction re-runs the search in place (pushState, no
 * reload — the dropdown the shopper is working in stays open); pagination
 * links navigate. Spejder Sport changes facets/sortings in the app after
 * handover; nothing here names a facet key.
 *
 * Category page specifics: Shopify still server-renders page N of its own
 * order (crawlers, no-JS), held invisible by the same CSS hold the ordering
 * script uses (data-relewise-order="full" → data-relewise-ready set here);
 * Relewise's page replaces it, breaker cells keep their slots, Shopify's
 * pagination is swapped for the Relewise one, and legacy Shopify filter
 * URLs (?filter.p.m.custom.brand=…) are translated into the contract above
 * so existing links (product-type circles, redirects) keep working.
 */
!(function () {
  'use strict';

  const grid = document.querySelector('[data-relewise-listing], [data-relewise-search-grid]');
  if (!grid) return;

  const cfg = window.spejder && window.spejder.relewise;
  /* Relewise switched off in the theme settings: the section rendered no
     Relewise chrome at all, so there is nothing to hand over to. */
  if (!cfg || !cfg.enabled) {
    grid.setAttribute('data-relewise-ready', ''); // release the CSS hold
    return;
  }
  /* window.Relewise itself is NOT checked here any more. It comes from
     whichever SDK loads last (the theme's relewise.min.js or the app embed's
     copy) and can legitimately be missing at this point — a slow or failed
     asset. Returning on the spot left the page with NEITHER filter bar: the
     Relewise chrome is empty until we render it and the Search & Discovery
     chrome stays hidden behind [data-sd-fallback] until we reveal it, which
     is exactly the "no filter pills at all" the client filmed (Trello
     "Filtrering og collection opfører sig mærkeligt", 2026-09-17). boot()
     waits for the SDK and hands a category page that never gets one to
     Search & Discovery. */
  const F = window.spejder.relewiseFacets; // optional — facets render only when loaded

  const mode = grid.getAttribute('data-relewise-listing') || 'search';
  const isCollection = mode === 'collection';
  const collectionId = grid.getAttribute('data-collection-id') || '';
  const location = grid.getAttribute('data-location') || (isCollection ? 'Custom Collection Page' : 'Search Results Page');
  const pageSize = parseInt(grid.getAttribute('data-page-size')) || 36;
  const caption = document.querySelector('[data-rw-count]') || document.querySelector('[data-results-count]');
  let captionRenders = 0; // a11y: announce the count from the second render on (user-triggered changes only)
  const productsContainer = grid.closest('[data-products-grid]');
  /* Breaker/promo cells Shopify placed in the grid (no product): kept at their
     slots when Relewise's page replaces the cards. */
  const breakers = [].slice.call(grid.children).map((el, i) => ({ el, i })).filter((b) => b.el.hasAttribute('data-grid-item') && !b.el.hasAttribute('data-url'));
  const templatesRoot = document.querySelector('[data-rw-templates]');
  const collectionComponent = document.querySelector('collection-component');
  const els = {
    chips: document.querySelector('[data-rw-chips]'),
    panels: document.querySelector('[data-rw-panels]'),
    active: document.querySelector('[data-rw-active]'),
    groups: document.querySelector('[data-rw-groups]'),
    footerActive: document.querySelector('[data-rw-footer-active]'),
    cta: document.querySelector('[data-rw-cta]'),
    sortList: document.querySelector('[data-rw-sort-list]'),
    sortLabel: document.querySelector('[data-rw-sort-label]'),
  };

  const searchConfig = () => {
    const sc = window.ShopifyRelewise && window.ShopifyRelewise.searchConfigurations;
    const key = isCollection ? 'collection' : 'search';
    return sc && sc[key] ? sc[key] : null;
  };

  /* Legacy Shopify filter/sort URLs → the Relewise contract, once, in place.
     Shopify's own filter params can never filter a Relewise result, so links
     that still carry them (old bookmarks, redirects, and every link the
     Search & Discovery fallback below emits) are rewritten rather than
     silently ignored. Keys that survive translation but are NOT a configured
     facet are applied as real product data filters instead (LEGACY_DATA_KEYS
     in run()) — dropping them showed the whole collection where the shopper
     had asked for a slice of it (Trello "Denne side henter ikke hvad der er i
     collection", 2026-09-17). */
  const LEGACY_SORT = {
    'best-selling': 'PopularityDesc', 'price-ascending': 'SalesPriceAsc', 'price-descending': 'SalesPriceDesc',
    'title-ascending': 'AlphabeticallyAsc', 'title-descending': 'AlphabeticallyDesc',
    'manual': 'Relevance', 'most-relevant': 'Relevance', 'relevance': 'Relevance', 'created-ascending': 'Relevance', 'created-descending': 'Relevance',
  };
  /* custom.sizes values are GROUP-PREFIXED ("Tøj/38-40", "Sko/42") — one
     facet for every size group, so one pill and multi-select across groups
     (app: app/lib/facet-keys.server.ts). Shopify's own size facet carries the
     bare size, so "38-40" matched no Relewise value at all: zero hits, an
     empty grid and — because Relewise then returns no values for any other
     facet — a filter bar with almost every chip gone. Expand the bare value
     across every configured group instead; Relewise ORs the values within a
     facet, so it lands on whichever group the product actually uses. The
     group list is the merchant's own, emitted by relewise-facet-templates. */
  const SIZE_GROUP_FALLBACK = ['Tøj', 'Sko'];
  const sizeGroups = () => {
    const raw = templatesRoot ? templatesRoot.getAttribute('data-rw-size-groups') || '' : '';
    const list = raw.split(',').map((s) => s.trim()).filter(Boolean);
    return list.length ? list : SIZE_GROUP_FALLBACK;
  };

  /* Keys whose selection is a GUESS across every group until the first answer
     comes back and says which of them the catalogue actually has — pruned in
     pruneExpansions() so the shopper ends up with one pill and one canonical
     Relewise URL, not six. */
  let expanded = {};

  const translateLegacyUrl = () => {
    const params = new URLSearchParams(window.location.search);
    expanded = { custom: {} };
    const keys = [...params.keys()];
    if (!keys.some((k) => k.indexOf('filter.') === 0) && !(params.get('sort_by') in LEGACY_SORT)) return;
    const out = new URLSearchParams();
    let priceMin = null; let priceMax = null;
    params.forEach((value, name) => {
      if (name === 'filter.v.price.gte') priceMin = parseInt(value, 10);
      else if (name === 'filter.v.price.lte') priceMax = parseInt(value, 10);
      else if (name === 'filter.v.availability') out.append('f.IsAvailable', value === '1' ? 'true' : 'false');
      else if (name === 'filter.p.product_type') out.append('f.ProductType', value);
      else if (name === 'filter.p.vendor') out.append('f.Vendor', value);
      else if (name === 'filter.v.option.farve') out.append('f.custom.farve', value.toUpperCase());
      else if (name === 'filter.v.m.custom.size_in_stock') {
        if (value.indexOf('/') !== -1) out.append('f.custom.sizes', value); // already prefixed
        else {
          sizeGroups().forEach((group) => out.append('f.custom.sizes', `${group}/${value}`));
          expanded.custom.sizes = true;
        }
      }
      else if (name.indexOf('filter.p.m.') === 0) out.append('f.' + name.slice('filter.p.m.'.length), value);
      else if (name.indexOf('filter.v.m.') === 0) out.append('f.' + name.slice('filter.v.m.'.length), value);
      else if (name.indexOf('filter.') === 0) { /* unknown Shopify facet — dropped */ }
      else if (name === 'sort_by' && value in LEGACY_SORT) { if (LEGACY_SORT[value] !== 'Relevance') out.set('sort_by', LEGACY_SORT[value]); }
      else out.append(name, value);
    });
    if (priceMin != null || priceMax != null) out.set('price', `${priceMin != null && !isNaN(priceMin) ? priceMin : ''}-${priceMax != null && !isNaN(priceMax) ? priceMax : ''}`);
    window.history.replaceState(null, '', `${window.location.pathname}${out.toString() ? '?' + out.toString() : ''}`);
  };

  /* Drops the guessed values the response did not list, in place, before
     anything renders — the search already ran, and a value that does not exist
     matched nothing, so the result is the same either way. */
  const pruneExpansions = (result, state) => {
    if (!expanded.custom || !expanded.custom.sizes || !F) return;
    expanded = {};
    const key = 'custom.sizes';
    const values = state.facets.selections[key] || [];
    if (!values.length) return;
    const items = (result && result.facets && result.facets.items) || [];
    const item = items.find((it) => (it.key || '') === key || (it.key || '') === `${cfg.shop}_${key}`);
    if (!item || !Array.isArray(item.available)) return;
    const real = {};
    item.available.forEach((a) => { if (a && a.value != null) real[String(a.value)] = true; });
    const kept = values.filter((v) => real[v]);
    if (kept.length === values.length) return;
    if (kept.length) state.facets.selections[key] = kept;
    /* Not one of the guesses exists — a size the catalogue has retired, or a
       link from a store that never had it. Relewise answers such a selection
       by ignoring it (verified 2026-09-18: six unknown values came back as the
       full, unfiltered collection), so leaving it in the URL would put six
       meaningless pills over a listing that is not filtered at all. Drop it:
       the result on screen is already the right one for no selection. */
    else delete state.facets.selections[key];
    const search = F.writeState(new URLSearchParams(window.location.search), state.facets).toString();
    window.history.replaceState(null, '', `${window.location.pathname}${search ? '?' + search : ''}`);
  };

  /* The app embed defines window.ShopifyRelewise (user + searchConfigurations)
     and, in most loads, window.Relewise itself, from an async initialiser that
     runs AFTER the deferred theme scripts. Measured on the live store
     (2026-09-18): the configuration is ready at ~1.5 s unthrottled but ~2.7 s
     on ordinary 4G — a quarter of a second inside the old 3 s budget, and
     past it on anything slower. Missing the budget did not degrade, it broke:
     the search ran with NO facets configured, so the page rendered a full,
     correct grid with an empty filter bar — no chips at all. The budget is
     wider now AND missing it is no longer a silent half-render: boot() hands
     the category page to Search & Discovery instead. */
  /* 4 s, NOT 6: the CSS hold on the server-rendered grid retires itself after
     6 s (spejder.css, data-relewise-order="full"), and the search plus its
     per-card fetches take ~2 s on top of whatever the wait cost. A 6 s budget
     therefore raced its own failsafe — config arriving at 5.9 s meant Shopify's
     cards appeared at 6 s and were then swapped for Relewise's at ~8 s, in
     front of the shopper. At 4 s Relewise always finishes inside the hold, so
     the page goes skeleton → final once; anything slower than that is handed
     to Search & Discovery, which is a complete, working page rather than a
     late reshuffle. Comfortably above the 2.7 s the configuration takes on
     ordinary 4G (measured 2026-09-18). */
  const APP_BUDGET_MS = 4000;
  const waitFor = (test, ms) => new Promise((resolve) => {
    const started = Date.now();
    (function poll() {
      if (test() || Date.now() - started > ms) return resolve();
      setTimeout(poll, 50);
    })();
  });

  const getUser = () => {
    /* Shared helper (assets/relewise-batch.js) waits for the app embed's global;
       testing for it synchronously here always found nothing (2026-09-16). */
    if (window.spejder && window.spejder.relewiseUser) return window.spejder.relewiseUser();
    if (window.ShopifyRelewise && typeof window.ShopifyRelewise.getUserAsync === 'function') {
      return window.ShopifyRelewise.getUserAsync().catch(() => Relewise.UserFactory.anonymous());
    }
    return Promise.resolve(Relewise.UserFactory.anonymous());
  };

  // The Liquid t/escape pipeline leaves one level of HTML entities (&quot; etc.)
  // in the data attributes — decode to plain text before displaying. DOMParser
  // documents are inert, and textContent strips any tag-shaped input, so this
  // is safe for user-derived strings like the search term.
  const readAttr = (name) => {
    const value = grid.getAttribute(name);
    if (!value) return '';
    return new DOMParser().parseFromString(value, 'text/html').documentElement.textContent || '';
  };

  /* The tab title carries the count the caption shows. Shopify's page_title
     counts Shopify's own search ("Søgning: 467 resultater …" over 234 shown),
     so the server renders the title without a count (data-title-base,
     snippets/spejder-search-title.liquid) and every render puts Relewise's
     in, worded like the caption; the rest of the title (" – Spejder Sport")
     stays. Search only: a collection page has no data-title-base. */
  const titleBase = readAttr('data-title-base');
  const titleTail = titleBase && document.title.indexOf(titleBase) === 0 ? document.title.slice(titleBase.length) : null;
  const setTitleCount = (countText) => {
    if (titleTail !== null && countText) document.title = `${readAttr('data-title-prefix')}: ${countText}${titleTail}`;
  };

  /* Card markup per handle. Three tiers before a network round trip:
       1. cards already in the grid (Shopify's server-rendered page overlaps
          Relewise's page heavily on the default view, and filter clicks keep
          most of the previous result) — reused, see serverCardHtml;
       2. this page view's memory cache (every card ever shown);
       3. sessionStorage (survives back/forward and pagination within the tab).
     Only the remainder is fetched, in parallel. */
  const cardCache = new Map();
  const SESSION_KEY = 'spejder-relewise-cards';
  /* 60, not 300: a stored card is ~46 KB, so 300 never fit sessionStorage's ~5 MB
     quota (writes failed silently around 110 cards), and the whole store was
     re-serialised on EVERY remembered card — ~400 ms per category page by the
     8th visit on a 4G phone profile (Trello "Load hastighed", 2026-10-05).
     60 still covers the current and previous result page for back/forward. */
  const SESSION_MAX = 60;
  /* A card is rendered for one membership state (member prices, the notify
     drawer's kundeklub boxes). Logging in or joining mid-tab changes that
     state, so cards stored under the other one are dropped, not reused. */
  const memberState = grid.getAttribute('data-member') || '';
  let session = {};
  try {
    const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}') || {};
    if (stored.member === memberState && stored.cards) session = stored.cards;
  } catch (e) { session = {}; }
  let persistTimer = 0;
  const persist = () => {
    persistTimer = 0;
    try {
      const keys = Object.keys(session);
      if (keys.length > SESSION_MAX) keys.slice(0, keys.length - SESSION_MAX).forEach((k) => delete session[k]);
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ member: memberState, cards: session }));
    } catch (e) { /* quota / private mode — memory cache still works */ }
  };
  const remember = (handle, html) => {
    cardCache.set(handle, html);
    session[handle] = html;
    // one write for all the cards a render remembers, not one per card
    if (!persistTimer) persistTimer = setTimeout(persist, 0);
  };
  /* A grid card is harvested from the LIVE page, after the theme's components
     ran. Its image slider (<hover-images>) has then been set up by Flickity:
     viewport, arrow buttons, .flickity-enabled. Re-inserted as HTML, that markup
     has no Flickity instance behind it, and initFlickity() bails on
     .flickity-enabled, so the arrows show but never move. A multi-image card
     then looks like it has one image (client card, 2026-10-02). Cache the card
     as the server sent it instead: each slider unwrapped back to its plain
     [data-hover-slide] cells. Parsed in a <template> so the copy's custom
     elements are not constructed. */
  const serverCardHtml = (el) => {
    const tpl = document.createElement('template');
    tpl.innerHTML = el.outerHTML;
    tpl.content.querySelectorAll('[data-hover-slider].flickity-enabled').forEach((slider) => {
      const cells = [...slider.querySelectorAll('[data-hover-slide]')];
      cells.forEach((cell) => {
        cell.classList.remove('flickity-cell', 'is-selected');
        cell.removeAttribute('style');
        cell.removeAttribute('aria-hidden');
      });
      slider.replaceChildren(...cells);
      slider.classList.remove('flickity-enabled');
      slider.removeAttribute('tabindex');
    });
    return tpl.innerHTML;
  };
  const harvestGrid = () => {
    grid.querySelectorAll('[data-grid-item][data-url]').forEach((el) => {
      const handle = (el.getAttribute('data-url') || '').split('?')[0].split('/products/')[1];
      if (handle && !cardCache.has(handle)) cardCache.set(handle, serverCardHtml(el));
    });
  };
  const fetchCard = (handle) => {
    if (cardCache.has(handle)) return Promise.resolve(cardCache.get(handle));
    if (session[handle]) { cardCache.set(handle, session[handle]); return Promise.resolve(session[handle]); }
    return fetch(`/products/${handle}?view=relewise-card`)
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => { if (html) remember(handle, html); return html; })
      .catch(() => '');
  };

  /* ---------- URL state ---------- */

  const readUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      params,
      term: grid.getAttribute('data-term') || params.get('q') || '',
      page: Math.max(1, parseInt(params.get('page')) || 1),
      sortId: params.get('sort_by') || 'Relevance',
      facets: F ? F.readState(params) : {selections: {}, price: null},
    };
  };

  const pageUrl = (target) => {
    const url = new URLSearchParams(window.location.search);
    if (target > 1) url.set('page', String(target));
    else url.delete('page');
    return `${window.location.pathname}?${url.toString()}`;
  };

  /** Push a new facet/sort state and re-run in place. */
  const navigate = (facetState, sortId) => {
    const params = new URLSearchParams(window.location.search);
    if (F && facetState) F.writeState(params, facetState);
    if (sortId !== undefined) {
      if (sortId && sortId !== 'Relevance') params.set('sort_by', sortId);
      else params.delete('sort_by');
    }
    params.delete('page');
    window.history.pushState(null, '', `${window.location.pathname}?${params.toString()}`);
    run();
  };

  /* ---------- rendering ---------- */

  /* Clones the Figma spejder-pagination markup that the section renders as a
     <template> (icons + translated strings included) and fills in the live
     numbers/URLs — the client-rendered page paginates with the exact designed
     component, popout page-jump and all. */
  const renderPagination = (hits, page) => {
    const totalPages = Math.ceil(hits / pageSize);
    const template = document.querySelector('[data-relewise-pagination]');
    if (totalPages <= 1 || !template) return null;

    const nav = template.content.cloneNode(true).querySelector('.spejder-pagination');
    const seen = Math.min(page * pageSize, hits);

    /* Every live number has its own <span data-rw-*> hook in the template
       (relewise-pagination-template.liquid) and is written with textContent.
       The former innerHTML string swap of sentinel digits kept the
       placeholder whenever the cloned markup was not byte-identical to what
       Liquid wrote — a client iPhone showed "Du har set 1000001 ud af 1801
       produkter" with the digits underlined like a tel: link (Trello
       v9hqYvsY, 2026-09-27). A hook cannot miss, and textContent discards
       any markup a browser wrapped around the digits. */
    const setNumber = (scope, hook, value) => {
      const el = scope.querySelector(hook);
      if (el) el.textContent = String(value);
    };
    setNumber(nav, '[data-rw-seen]', seen);
    setNumber(nav, '[data-rw-total]', hits);
    setNumber(nav, '[data-rw-page]', page);
    setNumber(nav, '[data-rw-pages]', totalPages);

    const prevLink = nav.querySelector('[data-rw-prev]');
    const prevDisabled = nav.querySelector('[data-rw-prev-disabled]');
    if (page > 1) {
      prevLink.setAttribute('href', pageUrl(page - 1));
      prevDisabled.remove();
    } else {
      prevLink.remove();
    }

    const nextLink = nav.querySelector('[data-rw-next]');
    const nextDisabled = nav.querySelector('[data-rw-next-disabled]');
    if (page < totalPages) {
      nextLink.setAttribute('href', pageUrl(page + 1));
      nextDisabled.remove();
    } else {
      nextLink.remove();
    }

    const list = nav.querySelector('[data-rw-page-list]');
    const currentProto = nav.querySelector('[data-rw-option-current]');
    const optionProto = nav.querySelector('[data-rw-option]');
    for (let i = 1; i <= totalPages; i++) {
      const proto = i === page ? currentProto : optionProto;
      const item = proto.cloneNode(true);
      setNumber(item, '[data-rw-option-page]', i);
      item.removeAttribute('data-rw-option-current');
      item.removeAttribute('data-rw-option');
      if (i !== page) item.querySelector('a').setAttribute('href', pageUrl(i));
      list.appendChild(item);
    }
    currentProto.remove();
    optionProto.remove();

    return nav;
  };

  const clearPagination = () => {
    const old = productsContainer && productsContainer.querySelector('.spejder-pagination');
    if (old) old.remove();
  };

  /* `limited`: the search was never sent (per-term limit, see run()), so the
     rescue row stays shut — it would only be one more Relewise request. */
  const showNoResults = (filtered, limited) => {
    // Figma's empty state is the message alone — no rescue content is designed,
    // so none is rendered (design rule: nothing outside the Figma).
    const message = readAttr('data-no-results');
    grid.innerHTML = '<div class="no-results"><p><strong></strong></p></div>';
    grid.querySelector('strong').textContent = message;
    clearPagination();
    setTitleCount(readAttr('data-caption-many').replace('2000001', '0'));
    // With filters active the nav stays: the shopper needs the pills / "Ryd
    // alle" to get out of the empty state. A term with no hits at all drops
    // the caption and the sort/filter nav, which have nothing to act on.
    if (!filtered) {
      if (caption) caption.remove();
      document.querySelectorAll('[data-collection-nav]').forEach((nav) => nav.remove());
      // Zero-results rescue: the 404 row (relewise-404.js, lazy mode).
      const rescue = document.querySelector('[data-relewise-404-lazy]');
      if (rescue && !limited) { rescue.hidden = false; document.dispatchEvent(new CustomEvent('relewise:no-results')); }
    } else if (caption) {
      caption.textContent = '';
    }
  };

  /* ---------- Search & Discovery fallback ----------
     A collection Relewise has no category for answers the termless category
     search with zero hits AND zero facet values, which used to leave the
     shopper with an empty filter bar over Shopify's own (kept) grid — looking
     like a theme bug rather than a sync gap (client card "/collection/nyheder
     har ikke filter", 2026-09-16). The section renders the full Search &
     Discovery chrome alongside the Relewise one; here we hand over to it.

     One way only: once Shopify owns the filtering, Relewise must stay out of
     the URL and off the grid, so `run` is a no-op afterwards. */
  let fallbackActive = false;
  /* translateLegacyUrl() rewrites Shopify's filter/sort params into the
     Relewise contract — right for a Relewise-served category, destructive for
     a fallback one, where those params ARE the live filter state (a refresh or
     a shared filtered link would come back unfiltered). Which one this is only
     becomes knowable after the first search, so on a collection carrying them
     the rewrite waits for the answer. */
  let pendingLegacyUrl = false;

  /* Every link the fallback chrome emits is a Shopify filter URL, and the
     hand-over decision is re-made from scratch on the next page load: a
     shopper who filtered inside the fallback landed on a Relewise-served page
     that translated those params instead of honouring them. Remember the
     hand-over for this collection (this tab only) so a legacy-param load goes
     straight back to Search & Discovery, and forget it again as soon as
     Relewise serves the same listing cleanly. */
  const FALLBACK_KEY = 'spejder:rw-sd-fallback';
  const rememberFallback = (on) => {
    try {
      if (on) window.sessionStorage.setItem(FALLBACK_KEY, window.location.pathname);
      else if (window.sessionStorage.getItem(FALLBACK_KEY) === window.location.pathname) window.sessionStorage.removeItem(FALLBACK_KEY);
    } catch (error) { /* private mode / blocked storage — the round-trip just stays unguarded */ }
  };
  const fallbackRemembered = () => {
    try { return window.sessionStorage.getItem(FALLBACK_KEY) === window.location.pathname; } catch (error) { return false; }
  };

  /* Hiding the Relewise chrome is not enough: collection.js resolves the
     slide-out with a first-match querySelector('[data-collection-sidebar-slide-out]')
     and both drawers carry it, so a merely-hidden Relewise drawer would keep
     winning and the fallback drawer would never open. It has to leave the DOM. */
  const stripRelewiseChrome = (root) => {
    (root || document).querySelectorAll('[data-rw-only]').forEach((el) => el.remove());
  };

  const activateShopifyFallback = () => {
    if (fallbackActive || !isCollection || !collectionComponent) return;
    fallbackActive = true;
    // Reveals [data-sd-fallback] (CSS in sections/collection.liquid). A class on
    // the component, not attributes on the elements, because collection-filters-form.js
    // rewrites the sidebar's innerHTML on every filter change.
    collectionComponent.classList.add('rw-sd-fallback');
    rememberFallback(true);
    stripRelewiseChrome();
    /* That same innerHTML rewrite re-inserts the Relewise drawer from the
       freshly fetched page, so the strip has to hold for the page's life. */
    const sidebar = collectionComponent.querySelector('[data-collection-sidebar]');
    if (sidebar && window.MutationObserver) {
      new MutationObserver(() => stripRelewiseChrome(sidebar)).observe(sidebar, {childList: true, subtree: true});
    }
    const src = grid.getAttribute('data-filters-form-src');
    if (src && !document.querySelector(`script[src="${src}"]`)) {
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      document.head.appendChild(script);
    }
  };

  /* ---------- the search ---------- */

  /* Keys a legacy URL can carry that are real Relewise product data keys but
     NOT facets the merchant configured. applySelections() can only write onto
     a facet the request actually asked for, so these used to fall on the
     floor: ?filter.p.product_type=Solbriller became ?f.ProductType=Solbriller
     and the shopper got the entire collection back with nothing to say a
     filter had been asked for. They are applied as ordinary product data
     filters instead — the app syncs both as GLOBAL (un-prefixed) data keys.
     Anything else that is neither a configured facet nor listed here cannot
     filter anything and is dropped from the URL (see orphans, below) so the
     page never claims to be filtered when it is not. */
  const LEGACY_DATA_KEYS = { ProductType: true, Vendor: true };

  /* One OR-ed equals filter per key (mustMatchAllConditions = false), so two
     values of the same key widen the result instead of cancelling out — the
     same semantics Relewise gives a facet. Guarded: window.Relewise is
     whichever SDK loaded last and the builder API is not guaranteed. */
  const addDataFilters = (f, state, keys) => {
    keys.forEach((key) => {
      const values = state.facets.selections[key] || [];
      if (!values.length || typeof f.addProductDataFilter !== 'function') return;
      f.addProductDataFilter(key, (c) => { values.forEach((v) => c.addEqualsCondition(v)); }, false);
    });
  };

  let seq = 0;

  /* Thrown instead of searching once this visitor has sent the same term 100
     times in the last minute (window.spejder.relewiseSearchAllowed, in
     assets/relewise-batch.js). */
  const searchLimited = new Error('Relewise search skipped: same term searched 100 times in the last minute');

  const run = async () => {
    if (fallbackActive) return;
    const mine = ++seq;
    const state = readUrl();
    const handleKey = `${cfg.shop}_ShopifyHandle`;
    const config = searchConfig();
    const facetsConfig = F && config ? F.configuredFacets(config.config, cfg.language) : [];
    const sortOptions = F ? F.sortOptions(config && config.config, cfg.language) : [];
    const sortOption = sortOptions.find((o) => o.id === state.sortId) || sortOptions[0] || null;
    /* Selections the configuration has no facet for. Only trustworthy once the
       configuration is actually here — without it EVERY selection would look
       unmatched and the URL would be wiped. */
    const configuredKeys = facetsConfig.map((f) => f.key);
    const unmatched = config ? Object.keys(state.facets.selections).filter((key) => configuredKeys.indexOf(key) === -1) : [];
    const dataFilters = unmatched.filter((key) => LEGACY_DATA_KEYS[key]);
    const orphans = unmatched.filter((key) => !LEGACY_DATA_KEYS[key]);
    /* Nothing can honour these — a facet the merchant has since removed, a
       mistranslated legacy param, a hand-edited URL. Left in place they would
       count towards "filtered" (no Search & Discovery hand-over on an empty
       result, an active-filter row the shopper cannot clear) while filtering
       nothing at all. Drop them and start over. */
    if (orphans.length) {
      const params = new URLSearchParams(window.location.search);
      orphans.forEach((key) => params.delete(`f.${key}`));
      const search = params.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${search ? '?' + search : ''}`);
      run();
      return;
    }
    const openPanel = window.spejder.facetChips ? window.spejder.facetChips.openPanelId() : '';
    /* Scroll positions survive the re-render: the open dropdown's list and the
       drawer (their elements are rebuilt, the shopper's place in them is not). */
    const openPanelEl = openPanel ? document.getElementById(openPanel) : null;
    const panelScroll = openPanelEl ? openPanelEl.scrollTop : 0;
    const drawerScrollEl = document.querySelector('[data-collection-sidebar-slide-out]');
    const drawerScroll = drawerScrollEl ? drawerScrollEl.scrollTop : 0;

    if (productsContainer) productsContainer.classList.add('is-loading');
    try {
      const builder = new Relewise.ProductSearchBuilder({
        language: cfg.language,
        currency: cfg.currency,
        displayedAtLocation: location,
        user: await getUser(),
      })
        .pagination((p) => p.setPageSize(pageSize).setPage(state.page))
        .setSelectedProductProperties({dataKeys: [handleKey]});
      if (isCollection) {
        /* Termless = Relewise's category-page mode; the Shopify collection id
           is the Relewise category id (app mapping). */
        if (collectionId || dataFilters.length) {
          builder.filters((f) => {
            if (collectionId) f.addProductCategoryIdFilter('ImmediateParent', [collectionId]);
            addDataFilters(f, state, dataFilters);
          });
        }
      } else {
        builder.setTerm(state.term);
        if (dataFilters.length) builder.filters((f) => addDataFilters(f, state, dataFilters));
      }

      /* The app's configuration adds the facets (and, in custom sorting mode,
         its own sorting — overridden right after with the shopper's choice). */
      if (config && config.config && typeof config.apply === 'function') {
        try { config.apply(builder); } catch (error) { if (window.console) console.warn('[relewise-listing] searchConfigurations.apply', error); }
      }
      if (F && sortOption) F.applySorting(builder, sortOption);

      const request = builder.build();
      /* One hit per PRODUCT. Variant colour (Option1) is in the search index
         since 2026-09-09, and a term that matches variants ("røde bukser")
         otherwise returns one hit per matching variant — the same product six
         times and an inflated hit count. maxVariantsPerProduct 0 would dedupe
         too but drops the variant match from ranking (a red wine glass
         outranks red trousers); 1 keeps it. Set on the built request, not via
         the builder: window.Relewise is whichever SDK loads last, and the app
         embed's copy (2.24.0) predates setVariantRequestSettings — its
         setVariantSearchSettings writes the legacy `variantSettings` key,
         which the API ignores for grouping. */
      request.settings.variantRequestSettings = {maxVariantsPerProduct: 1, sorting: 'GroupedByProduct'};
      /* Selections live in the URL, not in the app's configuration — written
         onto the built facet items (same post-build rule as above). */
      if (F) F.applySelections(request, state.facets);

      // Category pages are termless, so only the search page counts.
      const allowed = window.spejder.relewiseSearchAllowed;
      if (!isCollection && allowed && !allowed(state.term)) throw searchLimited;

      const searcher = new Relewise.Searcher(cfg.datasetId, cfg.apiKey, {serverUrl: cfg.serverUrl});
      const result = await searcher.searchProducts(request);
      if (mine !== seq) return; // a newer run superseded this one

      const redirect = result && result.redirects && result.redirects.length ? result.redirects[0].destination || result.redirects[0].url : null;
      if (redirect && redirect !== window.location.pathname + window.location.search) {
        window.location.assign(redirect);
        return;
      }

      const hits = (result && result.hits) || 0;
      const handles = ((result && result.results) || []).map((item) => item.data && item.data[handleKey] && item.data[handleKey].value).filter(Boolean);
      const filtered = F ? F.activeCount(state.facets) > 0 : false;

      /* A legacy ?filter.p.vendor= / ?filter.p.product_type= link whose value
         nothing in this listing carries (Shopify's vendor is the distributor,
         "Sports Group Denmark A/S", where the old link said "Toko"). These
         filters are never something the shopper picked — no chip produces
         them — so a dead end is nobody's intent: drop them, clean the URL and
         let the old link land on the collection it names. */
      if (!hits && dataFilters.length) {
        const params = new URLSearchParams(window.location.search);
        dataFilters.forEach((key) => params.delete(`f.${key}`));
        const search = params.toString();
        window.history.replaceState(null, '', `${window.location.pathname}${search ? '?' + search : ''}`);
        run();
        return;
      }

      /* Decided before the facets render, so the empty Relewise chrome is
         never painted on a collection we are about to hand to Shopify. */
      if (isCollection && !filtered && (!hits || !handles.length)) {
        activateShopifyFallback();
        return;
      }

      if (isCollection) rememberFallback(false);

      // Relewise does serve this collection, so the deferred rewrite is safe.
      if (pendingLegacyUrl) {
        pendingLegacyUrl = false;
        const before = window.location.search;
        translateLegacyUrl();
        if (window.location.search !== before) { run(); return; }
      }

      pruneExpansions(result, state);

      if (F && templatesRoot) {
        F.render(templatesRoot, result, facetsConfig, state.facets, els);
        F.renderSort(templatesRoot, sortOptions, sortOption ? sortOption.id : 'Relevance', els);
        // Re-open the chip dropdown the shopper was working in (ids are
        // index-based and stable across renders of the same facet set).
        if (openPanel && window.spejder.facetChips) {
          const toggle = document.querySelector(`[data-facet-popout-toggle][aria-controls="${openPanel}"]`);
          if (toggle) {
            window.spejder.facetChips.openPanel(toggle);
            const panel = document.getElementById(openPanel);
            if (panel) panel.scrollTop = panelScroll;
          }
        }
        if (drawerScrollEl && drawerScroll) drawerScrollEl.scrollTop = drawerScroll;
      }

      if (!hits || !handles.length) {
        showNoResults(filtered);
        return;
      }

      harvestGrid();
      const cards = (await Promise.all(handles.map(fetchCard))).filter(Boolean);
      if (mine !== seq) return;
      grid.innerHTML = cards.join('');
      breakers.forEach((b) => {
        const ref = grid.children[b.i] || null;
        grid.insertBefore(b.el, ref);
      });
      grid.querySelectorAll('[data-aos]').forEach((element) => element.classList.add('aos-animate'));
      grid.querySelectorAll('[data-grid-item]').forEach((element) => element.classList.add('aos-animate'));
      clearPagination();
      const pagination = renderPagination(hits, state.page);
      if (pagination) grid.insertAdjacentElement('afterend', pagination);

      const locale = grid.getAttribute('data-caption-locale');
      const formatted = locale ? hits.toLocaleString(locale) : String(hits);
      const countText = hits === 1 ? readAttr('data-caption-one') : readAttr('data-caption-many').replace('2000001', formatted);
      setTitleCount(countText);
      if (caption) {
        caption.textContent = countText;
        /* a11y: the count changes visually on every facet toggle; say it too. */
        if (captionRenders++ > 0 && window.theme && window.theme.announce) window.theme.announce(caption.textContent);
      }
    } catch (error) {
      // Relewise unavailable: reload without the client path is not possible here
      // (the SSR grid was not rendered) — show the no-results state as a soft fail.
      if (window.console && console.warn) console.warn('[relewise-listing]', error);
      /* Search has no server-rendered grid to fall back to; the category page
         keeps Shopify's grid and hands the filtering to Search & Discovery. */
      if (mine !== seq) return;
      if (isCollection) activateShopifyFallback();
      else showNoResults(false, error === searchLimited);
    } finally {
      const loader = productsContainer && productsContainer.querySelector('.grid__loader');
      if (loader) loader.remove();
      if (mine === seq && productsContainer) productsContainer.classList.remove('is-loading');
      grid.setAttribute('data-relewise-ready', ''); // release the CSS hold (category page)
    }
  };

  /* ---------- interactions ---------- */

  if (F) {
    const currentFacets = () => F.cloneState(readUrl().facets);

    // Capture phase: runs before spejder.js's delegated chip handler (which
    // would route the href through the Shopify AJAX filter path) and before
    // popout-select's option handling.
    document.addEventListener('click', (e) => {
      const el = e.target && e.target.closest && e.target.closest('[data-rw-action]');
      if (!el || el.tagName !== 'A') return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      const action = el.getAttribute('data-rw-action');
      e.preventDefault();
      e.stopPropagation();
      if (action === 'toggle') {
        // a11y: keyboard users get focus back on this option after the re-render.
        if (window.spejder && window.spejder.facetChips && window.spejder.facetChips.rememberFocus) window.spejder.facetChips.rememberFocus(el);
        navigate(F.toggleValue(currentFacets(), el.getAttribute('data-rw-key'), el.getAttribute('data-rw-value')));
      } else if (action === 'clear') {
        navigate({selections: {}, price: null});
      } else if (action === 'price-clear') {
        const s = currentFacets(); s.price = null; navigate(s);
      } else if (action === 'sort') {
        navigate(null, el.getAttribute('data-value'));
        const popout = el.closest('[data-popout-list]');
        if (popout) popout.classList.remove('popout-list--visible');
        const select = el.closest('popout-select');
        const toggle = select && select.querySelector('[data-popout-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
    }, true);

    // Drawer checkboxes.
    document.addEventListener('change', (e) => {
      const input = e.target;
      if (!input || input.getAttribute('data-rw-action') !== 'toggle' || input.type !== 'checkbox') return;
      navigate(F.toggleValue(currentFacets(), input.getAttribute('data-rw-key'), input.getAttribute('data-rw-value')));
    });

    const priceFrom = (minEl, maxEl, bounds) => {
      const min = minEl && minEl.value !== '' ? parseInt(minEl.value, 10) : null;
      const max = maxEl && maxEl.value !== '' ? parseInt(maxEl.value, 10) : null;
      const s = currentFacets();
      const atBounds = bounds && (min == null || min <= bounds.min) && (max == null || max >= bounds.max);
      s.price = (min == null && max == null) || atBounds ? null : {min, max};
      navigate(s);
    };

    // Chip price form ("Vis resultater").
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form || !form.hasAttribute || !form.hasAttribute('data-rw-price-form')) return;
      e.preventDefault();
      e.stopPropagation();
      priceFrom(form.querySelector('[data-rw-price-min]'), form.querySelector('[data-rw-price-max]'), null);
    }, true);

    // Drawer price: typed values (debounced) and the range slider.
    let priceTimer = null;
    document.addEventListener('input', (e) => {
      const input = e.target;
      if (!input || !input.hasAttribute || !(input.hasAttribute('data-field-price-min') || input.hasAttribute('data-field-price-max'))) return;
      const holder = input.closest('[data-range-holder]');
      if (!holder || !holder.closest('[data-rw-groups]')) return;
      clearTimeout(priceTimer);
      priceTimer = setTimeout(() => {
        const slider = holder.querySelector('range-slider');
        const bounds = slider ? {min: parseInt(slider.getAttribute('data-se-min'), 10), max: parseInt(slider.getAttribute('data-se-max'), 10)} : null;
        priceFrom(holder.querySelector('[data-field-price-min]'), holder.querySelector('[data-field-price-max]'), bounds);
      }, 600);
    });
    document.addEventListener('theme:range:update', (e) => {
      const slider = e.target && e.target.closest ? e.target.closest('range-slider') : null;
      if (!slider || !slider.closest('[data-rw-groups]')) return;
      const min = parseInt(slider.getAttribute('data-se-min-value'), 10);
      const max = parseInt(slider.getAttribute('data-se-max-value'), 10);
      const lo = parseInt(slider.getAttribute('data-se-min'), 10);
      const hi = parseInt(slider.getAttribute('data-se-max'), 10);
      if (isNaN(min) || isNaN(max)) return;
      const s = currentFacets();
      s.price = min <= lo && max >= hi ? null : {min, max};
      if (JSON.stringify(readUrl().facets.price) === JSON.stringify(s.price)) return;
      clearTimeout(priceTimer);
      priceTimer = setTimeout(() => navigate(s), 400);
    });

    // Drawer close buttons: without <collection-filters-form> nothing else
    // turns the close/CTA clicks into the theme's close event.
    document.addEventListener('click', (e) => {
      const close = e.target && e.target.closest && e.target.closest('[data-collection-sidebar-close]');
      if (!close || !collectionComponent) return;
      e.preventDefault();
      collectionComponent.dispatchEvent(new CustomEvent('theme:filter:close', {bubbles: false}));
    });

    window.addEventListener('popstate', () => run());
  }

  const carriesShopifyParams = [...new URLSearchParams(window.location.search).keys()]
    .some((key) => key.indexOf('filter.') === 0 || key === 'sort_by');

  /* Three ways a category page must end up on Search & Discovery rather than
     on a half-built Relewise one — all of them used to end up as a listing
     with no filter chrome whatsoever. */
  const boot = async () => {
    /* (1) The shopper filtered inside the fallback on this very collection and
       is coming back with Shopify's own params. Relewise never gets to
       translate them. */
    if (isCollection && carriesShopifyParams && fallbackRemembered()) {
      activateShopifyFallback();
      grid.setAttribute('data-relewise-ready', '');
      return;
    }

    /* One budget for both waits together — the SSR grid is already on screen
       (the CSS hold releases itself after 2.5 s), so a late swap costs a
       reflow, not a blank page. */
    const deadline = Date.now() + APP_BUDGET_MS;
    const left = () => Math.max(0, deadline - Date.now());

    // (2) No SDK: nothing here can run at all.
    if (!window.Relewise) await waitFor(() => window.Relewise, left());
    if (!window.Relewise) {
      if (isCollection) activateShopifyFallback();
      grid.setAttribute('data-relewise-ready', '');
      return;
    }

    // (3) No search configuration: there would be no facets to render.
    if (!searchConfig()) await waitFor(searchConfig, left());
    if (isCollection && !searchConfig()) {
      activateShopifyFallback();
      grid.setAttribute('data-relewise-ready', '');
      return;
    }

    if (isCollection && carriesShopifyParams) pendingLegacyUrl = true;
    else translateLegacyUrl();
    run();
  };
  boot();
})();
